/**
 * **The ownership permission gate.**
 *
 * Server-authoritative, ownership-only permissions, enforced as a **pre-`apply`
 * predicate** at every `applyCommand` chokepoint (roster + encounter rooms). It
 * answers one question — *may this actor issue this command?* — and the room
 * rejects a `false` as a `CommandError` no-op (no state change, no broadcast),
 * **without any reducer change** (the reducer stays pure; the gate is a wall in
 * front of it).
 *
 * The model:
 *   - **GM ⇒ god.** A `gm`-role slot may issue anything. (This is also what keeps
 *     the gate forward-compatible: `edit_combatant_base` and the GM directives are
 *     GM gestures, covered by the GM bypass.)
 *   - **Player ⇒ ownership-only.** A `player`-role slot may issue a command only
 *     when it acts on something its slot owns — its own pilots (roster) and the
 *     combatants its slot controls (encounter). **No turn gating** — Lancer has no
 *     fixed order — and a player **may edit its own pilots at any time, even
 *     mid-encounter** (the roster gate is pure pilot-ownership, no encounter check).
 *   - **An unknown actor** (no slot, e.g. a stray socket) is treated as a player
 *     owning nothing ⇒ denied everything it doesn't own (which is everything).
 *
 * ── THE LOAD-BEARING SUBTLETY (the whole point of this gate) ────────────────────
 *
 * The gate keys on the command's **initiating actor / source**, NOT on which
 * combatants the command happens to *touch*. A player's resolved-step attack
 * (`apply_resolved`) legitimately writes the **target's** HP — the target is an
 * enemy the player does *not* own — and that **must still pass**, because the
 * player owns the *attacker* (the source/initiator). So for an `apply_resolved`
 * the gate checks ownership of the **attacker** recorded in the steps' `to_hit`
 * step (the source), and **deliberately ignores** the `hp_delta`/`status_*` steps'
 * `target` (the touched combatants). Gating on touched cells would wrongly block
 * every player attack. See the `apply_resolved` case below.
 *
 * Pure + I/O-free: a predicate over the command, the actor's role, and the
 * relevant raw state (the owner map). Mirrored two ways (`canIssueRoster` /
 * `canIssueEncounter`) so each room calls the one for its domain.
 */
import type { EncounterIntent } from "@lancer-vtt/shared";
import type { SlotRole } from "./game/types";
import type { RosterCommand } from "./commands";
import type { RosterState } from "./types";
import type { EncounterCommand } from "./encounter/commands";
import type { Combatant, EncounterState } from "./encounter/types";
import { CASCADING_STATUS, controlsCombatant, encounterAnnotations, hasStatus } from "./encounter/types";

/**
 * What a room needs to know about the issuing actor to gate it: its slot role, or
 * `undefined` for an actor with no slot (an un-authenticated/stray socket). The
 * room derives this from the live `GameState.slots[actorId]?.role`.
 */
export type ActorRole = SlotRole | undefined;

/** A GM may issue anything; everyone else is gated by ownership. */
function isGm(role: ActorRole): boolean {
  return role === "gm";
}

// ── roster gate ──────────────────────────────────────────────────────────────────

/**
 * Does `actorId` own the pilot `pilotId` in `state`? The single "owns this pilot"
 * predicate — `pilot.owner === actorId` — shared by {@link canIssueRoster}'s default
 * case (the command-boundary wall) and `frontend/src/routes/home/ownedPilot.ts`'s
 * `ownedPilots` (the desktop's owned-pilot filter), so the rule is written once
 * rather than drifting into a second (or third) copy.
 * An unknown `pilotId` owns to nobody (`undefined?.owner` short-circuits to `false`).
 */
export function ownsPilot(state: RosterState, pilotId: string, actorId: string): boolean {
  return state.pilots[pilotId]?.owner === actorId;
}

/**
 * May `actor` (with role `role`) issue this **roster** command against `state`?
 *
 *   - GM ⇒ always.
 *   - `create_pilot` ⇒ a player may create a pilot **owned by itself** (`owner
 *     === actorId`); creating a pilot owned by someone else is denied.
 *   - `restore_pilot` ⇒ a player may restore a pilot it owns (`pilot.owner`).
 *   - `record_encounter_commit`, `apply_npc_wear`, `clear_npc_wear` ⇒ GM-only; they
 *     name no pilot to own. `full_repair_squad` ⇒ GM-only; it names many.
 *   - every other leaf carries a `pilotId` ⇒ a player may issue it only when it
 *     owns that pilot (`state.pilots[pilotId].owner === actorId`). A command naming
 *     an unknown pilot is denied (nothing to own).
 *   - `batch` ⇒ allowed only when **every** child is allowed (atomic; one
 *     disallowed child sinks the batch).
 *
 * **Players may edit their own pilots at any time, even mid-encounter**: this gate
 * is pure pilot-ownership, with no encounter/turn coupling.
 */
/** Named arguments for {@link canIssueRoster}. */
export type CanIssueRosterArgs = {
  command: RosterCommand;
  actorId: string;
  role: ActorRole;
  state: RosterState;
}

export function canIssueRoster(args: CanIssueRosterArgs): boolean {
  const { command, actorId, role, state } = args;
  if (isGm(role)) return true;
  switch (command.type) {
    case "batch":
      return command.commands.every((c) => canIssueRoster({ command: c, actorId, role, state }));
    case "create_pilot":
      // A player may only create a pilot it will own.
      return command.owner === actorId;
    case "restore_pilot":
      return command.pilot.owner === actorId;
    case "record_encounter_commit":
      // Roster-level, names no pilot: GM-only (the commit is a GM gesture).
      return false;
    case "apply_npc_wear":
    case "clear_npc_wear":
      // Roster-level allied-NPC ledger, names no pilot: GM-only (`decisions/0196`).
      return false;
    case "full_repair_squad":
      // Repairs every lancer at once: a GM gesture, whoever owns the pilots.
      return false;
    default:
      // Every remaining roster command names a `pilotId`; the player must own it.
      return ownsPilot(state, command.pilotId, actorId);
  }
}

// ── encounter gate ───────────────────────────────────────────────────────────────

/**
 * Does `actorId` control the combatant `combatantId` in `state`? The state-keyed
 * convenience over {@link controlsCombatant} — this gate holds ids, so it does the
 * map lookup and hands the combatant to the one shared predicate rather than
 * re-deriving control here (an id naming no combatant is controlled by nobody,
 * which the predicate answers).
 *
 * Reads the combatant's `controllerSlotId` LIVE off current state — spawn-set, but
 * **reassignable afterward** by the GM-only `set_controller` command, so this always
 * reads the CURRENT controller, never a value frozen at spawn. It **never** reads
 * the source pilot at check time (the encounter is self-contained; the source may be
 * edited/deleted without changing control).
 */
function controls(state: EncounterState, combatantId: string, actorId: string): boolean {
  return controlsCombatant(state.combatants[combatantId], actorId);
}

/**
 * Whether `combatant` currently carries the **Cascading** marker status — a single-arg
 * readability wrapper mirroring {@link "../../rules/src/encounter/shutdown" isShutDown}'s
 * shape, kept here (not the rules package) since it's only consumed by this gate.
 */
function isCascading(combatant: Pick<Combatant, "statuses">): boolean {
  return hasStatus(combatant.statuses, CASCADING_STATUS);
}

/**
 * `controls(...)`, additionally denying a non-GM whose target combatant is **Cascading**:
 * once the NHP has gone rogue, the unit is under GM control — a player's direct per-combatant
 * command against it (move/attack-support/turn-economy) is denied even though its
 * `controllerSlotId` is unchanged — Cascading is a **temporary overlay** that denies control
 * without writing the field, a genuinely different mechanism from a
 * `set_controller` reassignment, which DOES write the field, permanently, and is GM-only
 * regardless. The single guard every per-combatant `canIssueEncounter` case below shares, so the
 * Cascading check lives in one place rather than being repeated per case.
 */
function controlsAndNotCascading(state: EncounterState, combatantId: string, actorId: string): boolean {
  const c = state.combatants[combatantId];
  if (c !== undefined && isCascading(c)) return false;
  return controls(state, combatantId, actorId);
}

/**
 * The **attacker / source** combatant a resolved step-list acts on behalf of —
 * the `attacker` recorded in its `to_hit` step. This is the *initiator* of the
 * resolution, the combatant whose owner is permitted to fire it; the steps'
 * `target` ids (whose HP the resolution writes) are the *touched* combatants and
 * are **not** what the gate keys on. Returns `undefined` when no `to_hit` step is
 * present (a step list with no identifiable attacker — gated to GM-only).
 */
function resolvedAttacker(command: Extract<EncounterCommand, { type: "apply_resolved" }>): string | undefined {
  for (const step of command.steps) {
    if (step.op === "to_hit") return step.attacker;
  }
  return undefined;
}

/**
 * May `actor` (with role `role`) issue this **encounter** command against `state`?
 *
 *   - GM ⇒ always.
 *   - `add_combatant` ⇒ a player may spawn a combatant **its own slot controls**
 *     (`combatant.controllerSlotId === actorId`) — i.e. deploy its own mech; a
 *     player may not spawn a GM-owned/NPC or another player's combatant.
 *   - the per-combatant mutations (`set_hp`, `set_heat`, `apply_status`,
 *     `remove_status`, `set_combatant_name`, `move_token`, `teleport_token`,
 *     `remove_combatant`, `restore_pos`, `stand_up`, `restore_stand`, `activate_unit`,
 *     `boost`, `prepare`, `drop_prepare`, `spend_action_use`, `end_turn`) ⇒ a player may
 *     issue them only on a combatant it **controls** (this is what blocks a player from
 *     directly editing/moving an enemy) **and** only while that combatant is NOT
 *     **Cascading** ({@link controlsAndNotCascading}): once
 *     the NHP has gone rogue the unit is under GM control, even though its
 *     `controllerSlotId` is unchanged — Cascading is a temporary overlay that denies control
 *     without writing the field, distinct from a `set_controller` reassignment (which DOES
 *     write it, permanently, and is GM-only regardless — see {@link controlsAndNotCascading}'s
 *     doc).
 *   - `apply_resolved` ⇒ keyed on the **attacker/source** (see {@link
 *     resolvedAttacker}), NOT the touched targets — a player owning the attacker
 *     may fire an attack that writes an enemy's HP. The load-bearing case.
 *   - `activate_unit` ⇒ self-activation: a player may activate a combatant it **controls**
 *     — the one turn-family command that is a player's to
 *     make (*which of my units I activate is mine*). Its inverse `set_activation` stays
 *     GM-only (it carries the whole `ActivationState`, no per-combatant key; undo replays it
 *     off the gate, so the player's activation still round-trips).
 *   - `activate_unit` / `end_turn` ⇒ a player may start/end the turn of a unit it controls
 *     (the self-driven turn); the reducer still hard-enforces two-phase legality.
 *   - map authoring (`set_terrain`, `set_edge`) and the *global* round flow
 *     (`order_combatants`, `end_round`, `set_activation`) ⇒ GM-only (no per-combatant
 *     owner; a player is denied).
 *   - `restore_combatant` ⇒ a player may restore a combatant it controls.
 *   - `batch` ⇒ allowed only when **every** child is allowed.
 */
/** Named arguments for {@link canIssueEncounter}. */
export type CanIssueEncounterArgs = {
  command: EncounterCommand;
  actorId: string;
  role: ActorRole;
  state: EncounterState;
}

export function canIssueEncounter(args: CanIssueEncounterArgs): boolean {
  const { command, actorId, role, state } = args;
  if (isGm(role)) return true;
  switch (command.type) {
    case "batch":
      return command.commands.every((c) => canIssueEncounter({ command: c, actorId, role, state }));

    // A player may only spawn/restore a combatant its own slot will **own of record**.
    // Note these read the command's *payload* combatant, not one in `state` — the
    // combatant does not exist yet.
    //
    // **These two deliberately do NOT use `controlsCombatant`**, and that is a considered
    // exception rather than an oversight. They look like a "does this slot control C?"
    // question, which would be right if control were a scalar. Under co-control they are
    // really "which slot OWNS C?": the question a spawn gate asks is *whose books does this
    // new unit land on*, and
    // ownership of record is what attribution, `pcCountFor` party size, and deploy
    // inheritance all key on. Routing them through the membership predicate would silently
    // let a player create a unit **owned by another slot** merely by listing itself as a
    // co-driver — a gate getting looser as a side effect of a widening aimed at something
    // else. Spawning a shared unit stays a GM gesture via the GM-only `set_controller`.
    case "add_combatant":
      return command.combatant.controllerSlotId === actorId;

    case "restore_combatant":
      return command.combatant.controllerSlotId === actorId;

    // Per-combatant mutations: the player must control the named combatant.
    case "set_hp":
    case "set_heat":
    case "apply_status":
    case "remove_status":
    case "set_combatant_name":
    case "move_token":
    // `teleport_token` is a player gameplay command
    // for its OWN unit — the same shape as `move_token` above: a player may teleport a unit
    // it CONTROLS, the reducer separately HARD-enforces the RAW legality (acting turn, not
    // Immobilized/Stunned, the frozen `base.teleport` range wall, endpoint-free, budget).
    case "teleport_token":
    case "remove_combatant":
    case "restore_pos":
    // `stand_up` is a player gameplay command for its OWN
    // unit — the same shape as `move_token`/`boost` above: a player may stand a unit it
    // CONTROLS, the reducer separately HARD-enforces the RAW legality (acting turn, Prone,
    // not Immobilized/Stunned/Shut Down/Down and Out, standard move unspent). Its inverse
    // `restore_stand` stays owner-allowed too, same as `restore_pos`.
    case "stand_up":
    case "restore_stand":
      // Cascading: a non-GM's direct command against a combatant that has gone
      // rogue is denied — see `controlsAndNotCascading`.
      return controlsAndNotCascading(state, command.combatantId, actorId);

    case "apply_resolved": {
      // THE LOAD-BEARING CASE: gate on the *attacker/source* (the initiator), NOT
      // the touched targets. A player's attack writes the enemy target's HP — that
      // is legitimate because the player owns the attacker. An apply_resolved with
      // no identifiable attacker (no to_hit step) is GM-only.
      const attacker = resolvedAttacker(command);
      return attacker !== undefined && controls(state, attacker, actorId);
    }

    // `stamp_activated`/`unstamp_activated` are system-issued
    // exactly like `spend_action`: the room folds them alongside an accepted Dismount/Eject/
    // Mount resolution (the double-activation guard), never a player's direct command. A player
    // drives them only by firing the bridge-action intent, gated by `canResolveIntent` on the
    // intent's own actor. GM-only here.
    case "stamp_activated":
    case "unstamp_activated":
      return false;

    case "activate_unit":
      // Self-activation: *which of my units I activate this round is mine* — a player may
      // activate a unit it CONTROLS.
      // Paired with `end_turn` below, this lets a player drive its OWN unit's turn start→end;
      // the *global* round flow (`end_round`/`order_combatants`/`set_activation`) stays GM-only
      // below (Lancer's round is GM-driven). Like every per-combatant mutation, gate on the
      // named combatant the player controls; an unknown id is denied by `controls`. The
      // reducer separately HARD-enforces RAW legality (off-side / mid-turn / acted) —
      // ownership and turn-legality are distinct walls. Cascading denies too — see
      // `controlsAndNotCascading`.
      return controlsAndNotCascading(state, command.combatantId, actorId);

    case "boost":
      // Boost is a player gameplay command for its OWN unit — the move-budget twin of
      // `move_token`/`activate_unit` above: a player may Boost a unit it CONTROLS (gate on
      // the named combatant; an unknown id is denied by `controls`).
      // The reducer separately HARD-enforces the RAW legality (active-actor + a Quick action
      // left), so ownership and turn-legality stay distinct walls. Cascading denies
      // too — see `controlsAndNotCascading`.
      return controlsAndNotCascading(state, command.combatantId, actorId);

    // `prepare`/`drop_prepare` are player gameplay commands
    // for their OWN unit — the same shape as `boost` just above: a player may Prepare (or
    // drop the hold on) a unit it CONTROLS. The reducer + room separately HARD-enforce the
    // RAW legality (active actor, Quick-action budget, once-per-turn ledger, Ordnance/
    // Loading/status legality for the readied verb), so ownership and turn-legality stay
    // distinct walls — mirroring the "owner OR GM" release model one level down (preparing
    // and dropping are squarely the owner's own-turn call).
    // Cascading denies too — see `controlsAndNotCascading`.
    case "prepare":
    case "drop_prepare":
      return controlsAndNotCascading(state, command.combatantId, actorId);

    // `spend_action_use` is a player
    // gameplay command for its OWN unit — the same shape as `boost`/`prepare` just above: a
    // player may spend a round/scene action use of a unit it CONTROLS. The reducer
    // separately HARD-enforces the round/scene cap (`canUseRoundAction`/`canUseSceneAction`),
    // so ownership and legality stay distinct walls, mirroring `boost`'s self-enforcing shape.
    // Cascading denies too — see `controlsAndNotCascading`.
    case "spend_action_use":
      return controlsAndNotCascading(state, command.combatantId, actorId);

    case "end_turn":
      // Self-ended turn: the counterpart to `activate_unit` above — a player
      // ends the turn of a unit it CONTROLS, matching the inspector header's END TURN / PASS.
      // Without this, a player's END applies OPTIMISTICALLY on its own screen but is silently
      // dropped server-side (no broadcast), desyncing the GM (who has to re-click END). Keyed on
      // the named combatant; the reducer still HARD-enforces the two-phase legality
      // (`activating == X`) regardless of who issues it, so ownership and turn-legality stay
      // distinct walls. `end_round` (no per-combatant key — global round flow) stays GM-only below.
      // Cascading denies too — see `controlsAndNotCascading`.
      return controlsAndNotCascading(state, command.combatantId, actorId);

    // Annotations are the app's
    // FIRST player-writable map-surface primitive — unlike rooms/terrain (GM-only
    // authoring, below), everyone may draw. `add_annotation` ⇒ a player may add an
    // annotation it will AUTHOR (`annotation.authorSlotId === actorId`, the exact
    // `controllerSlotId`-style forge-proof: the client sets the field, but this gate
    // checks it against the server-stamped actor). `remove_annotation`/`set_annotation`
    // (recolor/replace) ⇒ a player may touch only an annotation it OWNS — keyed on the
    // EXISTING annotation's authorSlotId in `state`, not the command body (an unknown
    // id has no authorSlotId to match ⇒ denied, nothing to own, mirrors the roster's
    // "unknown pilot" case). GM bypasses all three via the top `isGm` check — owner + GM
    // erase, with no "anyone erases anyone's" shared-whiteboard mode.
    case "add_annotation":
      return command.annotation.authorSlotId === actorId;

    case "remove_annotation":
      return encounterAnnotations(state)[command.annotationId]?.authorSlotId === actorId;

    case "set_annotation":
      return encounterAnnotations(state)[command.annotation.id]?.authorSlotId === actorId;

    // The mutable base is a **GM gesture**:
    // editing a combatant's frozen base mid-fight (e.g. handing it a combat-only
    // weapon) is the GM's to make, not a player's. Both the public edit
    // and its internal inverse are GM-only (the GM bypass at the top already lets a
    // GM through; a player is denied here). The cross-domain "persist to base mech"
    // half is a roster `equip_weapon`, gated separately by `canIssueRoster` (a player
    // editing its OWN pilot would pass there, but the encounter half pins this gesture
    // GM-only in practice).
    case "edit_combatant_base":
    case "restore_combatant_base":
    // The GM state setters (`gm-state-setters`): structure, stress, burn and one affordance-
    // ledger entry by fiat. A player's own unit moves these through the rules (damage, the
    // structure/stress tables, activations), never by setting them — GM-only, like a base edit.
    case "set_structure":
    case "set_stress":
    case "set_burn":
    case "set_affordance_state":
    // The GM reload setter (`decisions/0209`): a player reloads through Stabilize/Reload.
    case "set_weapon_loaded":

    // Door toggle: GM-only, free, manual-tier — doors are GM-narrated terrain, not a
    // player gameplay command. A player never issues `set_door_state`, even for a door
    // whose `controllerSlotId` names it: `set_controller` makes a player-controlled
    // object genuinely reachable, but door state stays GM authoring regardless of who
    // controls the object it's attached to. This is the STRICTER side of the split —
    // gating the client toggle on `canControl` instead produces a dead button (see
    // `permissions.audit.ts`'s `set_door_state` row). Its internal inverse
    // `restore_door_state` stays GM-only too, reached only via the GM's top bypass.
    case "set_door_state":
    case "restore_door_state":

    // `set_concealed` (fog-of-war) is a GM gesture exactly like
    // `edit_combatant_base`: deciding what a player's client is allowed to see is the
    // GM's call, not a player's — a player is denied here (the GM bypass at the top
    // already lets a GM through). No per-combatant ownership carve-out: a player never
    // conceals even its OWN unit (that would let it hide from the GM's bookkeeping).
    case "set_concealed":

    // `set_disclosure`/`set_hp_disclosure` are GM gestures exactly like `set_concealed` — deciding
    // how much of a combatant's stat block (or whether current HP streams live) a
    // player's client is shown is the GM's manual call, not a player's. No per-combatant
    // ownership carve-out, same reasoning as `set_concealed`: a player never sets its OWN
    // unit's disclosure either. (The OTHER writer — a successful Scan's reveal step —
    // doesn't go through this gate at all; it rides `apply_resolved`'s scanner-source
    // gate below, same as a resolved attack writing the target's HP.)
    case "set_disclosure":
    case "set_hp_disclosure":

    // `set_token_image` is a GM gesture in the same family: what picture a unit wears on the
    // board is the GM's call (`projects/token-art.md` Phase 3). A player's own mech art already
    // comes from its roster upload, so there is no ownership carve-out here either.
    case "set_token_image":

    // `set_controller` is a GM gesture exactly like
    // `edit_combatant_base`/`set_concealed`/`set_disclosure`: reassigning who controls a
    // combatant (and optionally its side) is the GM's manual call, not a player's — no
    // per-combatant ownership carve-out. A player never reassigns even a unit it already
    // controls (that would let it hand itself an enemy, or give its own unit away
    // unsupervised). Not a force-tier command — see the SetControllerCommand docstring:
    // it has no gameplay sibling to be the force-override OF (no rules-driven path ever
    // changes a controller); the override is still a labelled, GM-only-at-the-gate command.
    case "set_controller":

    // `grant_ruling`/`restore_statuses` are GM gestures in the same
    // family: minting a mechanically-real boon or bane on a unit is the GM's call. This is
    // deliberately NOT a widened `apply_status` — that one is "player-conditional", so hanging
    // a `RulingSpec` off it would let any player mint real mechanics on a unit they control.
    // No per-combatant ownership carve-out for the same reason.
    case "grant_ruling":
    case "restore_statuses":
    // `grant_move` is the same family: handing a unit extra movement
    // is the GM's call, never a player's. A player moves within the rules via `move_token` and
    // buys more via the RAW `boost` command, both of which stay hard-enforced.
    case "grant_move":

    // Authoring placement — the edit-mode counterpart to `move_token`
    // that drops the speed/path budget. Free repositioning is a GM authoring power, kept
    // GM-only here (a player places/moves its own token within the rules via `move_token`);
    // its `restore_pos` inverse stays owner-allowed above, reached by the GM's top bypass.
    case "place_token":
    // The GM override's walk (`decisions/0209`): minted by the forced move referee, GM-only like
    // the `place_token` it sits beside.
    case "walk_token":

    // Encounter-level meta (the encounter editor: name + grid mode + grid dimensions) is GM-only.
    // `set_environment` (the COMBAT.app TERRAIN window's
    // Environment section) joins this cluster: a scene-wide hazard tag is a GM authoring
    // gesture, the same posture as the grid mode/dimensions it sits beside.
    case "rename_encounter":
    case "set_grid_mode":
    // Ending (and un-ending) the fight is table-wide flow, like `end_round`: GM-only.
    case "end_combat":
    case "reopen_combat":
    case "resize_grid":
    case "set_environment":
    // `set_ground` — the map ground the terrain art paints; a scene-wide authoring write.
    case "set_ground":

    // `set_sitrep` (Core pp. 267–273) authors the whole
    // victory-condition config in bulk — the same GM authoring posture as
    // `set_environment` just above (a scene-wide config write, not a per-combatant
    // mutation a player could own).
    case "set_sitrep":

    // Map authoring + turn/initiative are GM-only (no per-combatant owner). Rooms are a
    // purely-visual authoring primitive — GM-only like the terrain layer; a player never
    // authors the board.
    case "set_terrain":
    case "set_edge":
    case "apply_terrain_map":
    case "set_room":
    case "remove_room":
    case "order_combatants":
    // `end_round` is the GM's to drive: a player's stake in the turn is
    // *which of my units I activate, and ending its turn* (`activate_unit` + `end_turn`,
    // relaxed above); advancing the *round* — clearing the acted set, flipping the opening
    // side per RAW R5 — is global round flow with no per-combatant owner, so it stays GM-only.
    case "end_round":
    // `set_activation` (the Tier-2 override + the inverse of every Tier-1 command) stays GM-only:
    // it carries the WHOLE `ActivationState` (active side + activating + acted set) + round +
    // budget with no per-combatant key to verify ownership against — letting a player issue an arbitrary
    // one would hand them the entire turn/activation state to rewrite. Note this does NOT
    // block a player UNDOING its own activation: undo/redo replay the inverse OFF the gate
    // (`commandRoom.undo` → `history.undo`, not `applyCommand`), so a player's `activate_unit`
    // round-trips even though its `set_activation` inverse is gate-denied for a player.
    case "set_activation":
    // Action-economy budget: `spend_action`/`restore_budget` are system-issued
    // (the room folds them alongside an accepted resolution / as undo inverses), never a
    // player's direct command — GM-only here (a player drives the economy only by firing
    // a resolution intent, gated by `canResolveIntent`).
    case "spend_action":
    // `spend_reaction` is system-issued exactly like `spend_action`:
    // the room folds it as a sibling in an accepted attacker's resolution batch (Brace's defender
    // reaction), never a player's direct command. A player drives it only by being Braced inside a
    // resolution the GM/attacker fired. GM-only here.
    case "spend_reaction":
    // `record_move_reaction_decline` is system-issued the same way: `refereeMove` folds it into
    // a `request_move` plan when a checkpoint answer is "decline". A player declines by answering
    // the prompt (a resolution intent), never by sending this command. GM-only here.
    case "record_move_reaction_decline":
    // `set_split_move` too: `refereeMove` holds and releases a Covering Fire split with it.
    case "set_split_move":
    case "restore_budget":
    // `grant_overcharge` is system-issued exactly like `spend_action`: the
    // room folds it alongside an accepted Overcharge intent (the +1 Quick action), never a
    // player's direct command. A player drives Overcharge only by firing the `request_overcharge`
    // intent, gated by `canResolveIntent` on its `actor`. GM-only here.
    case "grant_overcharge":
    // `grant_action_allowance` (Initiative) is system-issued
    // exactly like `grant_overcharge`: the room folds it alongside an accepted `request_activate`
    // naming a bonus-action-granting entity id (the +1 Quick action), never a player's direct
    // command. A player drives it only by firing `request_activate`, gated by `canResolveIntent`
    // on its `actor`. GM-only here.
    case "grant_action_allowance":
    // `open_attack` / `close_attack` are system-issued exactly like `spend_action`:
    // the room folds them into an accepted attack shot batch (open on the opener, close on
    // auto-close / `end_attack`), never a player's direct command. A player drives a Barrage or a
    // lock-open Skirmish only by firing the `request_barrage`/`request_attack` / `end_attack`
    // intents, gated by `canResolveIntent` on the attacker. GM-only here.
    case "open_attack":
    case "close_attack":
    // `convert_barrage_to_skirmish` likewise: folded for an accepted `stop_barrage_as_skirmish`.
    case "convert_barrage_to_skirmish":
      return false;
  }
}

/**
 * Who may send each {@link EncounterIntent}, read by {@link canResolveIntent} right after its GM
 * early-return. The intent-side twin of `./permissions.audit.ts`'s `ENCOUNTER_COMMAND_CLASS`, and
 * unlike that table this one is load-bearing: the gate denies every `"gm-only"` row to a non-GM.
 *
 * **The whole point is the `satisfies`.** A new `EncounterIntent` member without a row here fails
 * `bun run typecheck`. Before this table the denies were four hand-written `if`s, and a new GM-only
 * verb whose deny nobody wrote fell through to the source-derivation branch and was allowed to any
 * player controlling its `.actor`.
 *
 *   - `"gm-only"` — denied to every non-GM, no ownership carve-out.
 *   - `"player-conditional"` — the GM, or the player who controls the intent's source combatant.
 *     Which field names the source (`.attacker` / `.combatantId` / `.actor`) is the gate's
 *     source-derivation step, a separate axis this table deliberately does not encode.
 *
 * The `request_` / `force_` prefix does not encode role (`request_gm_damage` is GM-only), so classify
 * a new member by what the gate should do, not by its name. A GM override stays a separate named
 * verb (`decisions/0042`); this table classifies verbs, it never adds a shared force flag.
 */
export type IntentSenderClass = "gm-only" | "player-conditional";

export const INTENT_SENDER_CLASS = {
  // ── gm-only ──
  // The GM override wrapper (`decisions/0209`), exactly like `place_token`. A player never forces
  // an action; it sends the wrapped gameplay intent within the rules. Load-bearing: the wrapper's
  // whole point is skipping legality, so this row is the wall.
  gm_force: "gm-only",
  // GM-only for a **structurally different reason** than the override above —
  // worth stating, because "GM-only" reads the same at the call site and the reasons are not
  // interchangeable. It has a real source combatant and is denied because a player must send the
  // rules-following gameplay intent instead. This one has **no source combatant at all**: the
  // GM is the initiator, so the intent names only its target. The gate keys on the source and there
  // is none, which puts it squarely in `canResolveIntent`'s own documented "an intent with no
  // identifiable source is GM-only" clause. Falling through to the `.actor` branch would read
  // `undefined` and deny by accident rather than by rule — the right answer for the wrong reason,
  // and one that would silently become a hole if that branch ever grew a fallback.
  request_gm_damage: "gm-only",
  // ── player-conditional, source keyed on `.attacker` ──
  request_attack: "player-conditional",
  request_barrage: "player-conditional",
  end_attack: "player-conditional",
  stop_barrage_as_skirmish: "player-conditional",
  // ── player-conditional, source keyed on `.combatantId` ──
  request_move: "player-conditional",
  request_end_turn: "player-conditional",
  // ── player-conditional, source keyed on `.actor` ──
  request_activate: "player-conditional",
  request_stabilize: "player-conditional",
  request_overcharge: "player-conditional",
  request_full_tech: "player-conditional",
  request_hide: "player-conditional",
  request_search: "player-conditional",
  request_grapple_break: "player-conditional",
  request_grapple_release: "player-conditional",
  request_grapple_contest: "player-conditional",
  request_disengage: "player-conditional",
  request_shutdown: "player-conditional",
  request_boot_up: "player-conditional",
  request_self_destruct: "player-conditional",
  request_dismount: "player-conditional",
  request_eject: "player-conditional",
  request_mount: "player-conditional",
  request_jockey: "player-conditional",
  request_jockey_menu: "player-conditional",
  request_shake_off: "player-conditional",
  request_ride: "player-conditional",
  request_ride_dismount: "player-conditional",
  request_reload: "player-conditional",
  request_deploy_drone: "player-conditional",
  request_deploy_mine: "player-conditional",
  request_detect_mine: "player-conditional",
  request_disarm_mine: "player-conditional",
  request_detonate_mine: "player-conditional",
  request_engage_compcon: "player-conditional",
  request_take_back_compcon: "player-conditional",
} as const satisfies Record<EncounterIntent["type"], IntentSenderClass>;

/** The members {@link INTENT_SENDER_CLASS} marks `"gm-only"`, derived off the table. */
type GmOnlyIntentType = {
  [K in keyof typeof INTENT_SENDER_CLASS]: (typeof INTENT_SENDER_CLASS)[K] extends "gm-only" ? K : never;
}[keyof typeof INTENT_SENDER_CLASS];

/**
 * Whether {@link INTENT_SENDER_CLASS} marks this intent `"gm-only"`. A type predicate so the gate's
 * source derivation narrows past the GM-only members, which is what keeps its `.actor` read
 * compiling without `request_gm_damage` (no source) or `gm_force` (a wrapper) in its way.
 */
function isGmOnlyIntent(intent: EncounterIntent): intent is Extract<EncounterIntent, { type: GmOnlyIntentType }> {
  return INTENT_SENDER_CLASS[intent.type] === "gm-only";
}

/**
 * May `actor` (with role `role`) fire this resolution **intent** against `state`?
 *
 * The pre-resolution sibling of `canIssueEncounter`'s `apply_resolved` case: an
 * `encounter_intent` is gated **before any dice are drawn**, so a denied resolution
 * never resolves. Like every encounter gate it keys on the **source / initiator** —
 * a `request_attack`'s `attacker` or a `request_activate`'s `actor` — and **NOT** on
 * the combatants the resolution will go on to write (the resolved attack legitimately
 * writes the *target's* HP, an enemy the player doesn't own; that must pass because the
 * player owns the *attacker*). GM ⇒ always; a `"gm-only"` row of {@link INTENT_SENDER_CLASS} ⇒ never for
 * a non-GM; an intent with no identifiable source is GM-only.
 *
 * **Cascading:** when the SOURCE combatant is Cascading, a
 * non-GM may fire ONLY `request_shutdown` through it — every other intent is denied, RAW's
 * "the only action a pilot in a cascading mech can take." Keyed on the source, not the
 * target — attacking a Cascading enemy through a different unit is unaffected.
 */
/** Named arguments for {@link canResolveIntent}. */
export type CanResolveIntentArgs = {
  intent: EncounterIntent;
  actorId: string;
  role: ActorRole;
  state: EncounterState;
}

export function canResolveIntent(args: CanResolveIntentArgs): boolean {
  const { intent, actorId, role, state } = args;
  if (isGm(role)) return true;
  if (isGmOnlyIntent(intent)) return false;
  // Barrage is a player gameplay command keyed on the attacker, like `request_attack` — a
  // player may Barrage with a combatant it controls. Stabilize keys on its `actor`
  // (self-maintenance on a unit the player controls), like `request_activate` — both fall
  // through to the `.actor` branch. `request_hide` keys on its `actor` (you Hide a unit you
  // control) — also the `.actor` branch.
  // `request_grapple_break`/`request_grapple_release` key on their
  // `.actor` the same way (you break/release a grapple through a unit you control).
  // `request_grapple_contest` keys on its `.actor` too — EITHER party of a same-Size grapple
  // may dispatch the tiebreak through a unit it controls, same own-actor set as the
  // break/release pair.
  // `request_move` keys on its `.combatantId`
  // (the mover) — the direct wire twin of `move_token`'s own `combatantId`, not an `.actor`.
  // `request_end_turn` keys on the SAME `.combatantId` field, for the identical reason — the
  // direct wire twin of `end_turn`'s own `combatantId` (`decisions/0164` Decision 2).
  const source =
    intent.type === "request_attack" || intent.type === "request_barrage" || intent.type === "end_attack" || intent.type === "stop_barrage_as_skirmish"
      ? intent.attacker
      : intent.type === "request_move" || intent.type === "request_end_turn"
      ? intent.combatantId
      : intent.actor;
  if (source === undefined) return false;
  // Cascading gate: once the SOURCE combatant has cascaded (gone rogue under GM control via
  // `ai_cascade_check`), a non-GM acting THROUGH it may fire ONLY
  // `request_shutdown` — RAW's "this is the only action a pilot in a cascading mech can take."
  // Keyed on the SOURCE/initiator like the rest of this gate, NOT the target: a player attacking
  // a Cascading ENEMY with a different, non-Cascading unit is unaffected (that intent's source is
  // the attacker, not the cascading enemy). The GM already passed via the top `isGm` bypass — that
  // bypass IS the takeover.
  const sourceCombatant = state.combatants[source];
  if (sourceCombatant !== undefined && isCascading(sourceCombatant) && intent.type !== "request_shutdown") {
    return false;
  }
  return controls(state, source, actorId);
}
